'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import { Container } from '@/components/primitives/Container/Container';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { CSLPFieldMapping, IFilteredList } from '@/.generated';
import { toPascalCase } from '@/utils/string-utils';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeDownload, getDocumentNameFromUrl, trackAdobeClick } from '@/utils/adobe';
import { defaultVariants } from './FilteredList.styles';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { FileIcon } from '@/components/primitives/FileIcon';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';

type FilteredListProps = WithMetadata<IBaseComponentProps> & IFilteredList;

type OptionItem = NonNullable<IFilteredList['reference']>[number];
type FileRow = NonNullable<OptionItem['file_table_section']>[number];
type FileField = FileRow['row_file_1'];

const Default = (props: FilteredListProps) => {
  const {
    componentName,
    _metadata,
    default_option_label,
    reference,
    inline_navigation_id,
    hide_component,
  } = props;

  const {
    base,
    filterRow,
    select,
    content,
    tableWrapper,
    table,
    row,
    cell,
    cellFile,
    cellEmpty,
    fileLink,
  } = defaultVariants();

  const options: OptionItem[] = reference ?? [];
  const hasPlaceholder = !!default_option_label;

  const [selectedKey, setSelectedKey] = useState<string>(hasPlaceholder ? '' : '0');
  const selectedOption = selectedKey !== '' ? options[Number(selectedKey)] : undefined;
  // Dedupes the open Click: a native select fires mousedown more than once per open gesture
  const openTrackedRef = useRef(false);

  if (hide_component) return null;

  // Component identifier shared by both filter Click events
  const clickLocation = `${componentName}_${_metadata?.uid}`;

  // Track opening the filter dropdown once per open cycle (mousedown can fire repeatedly)
  const handleOpen = (e: React.MouseEvent<HTMLSelectElement>) => {
    if (openTrackedRef.current) return;
    openTrackedRef.current = true;
    trackAdobeClick(
      {
        clickName: default_option_label || 'Filter options',
        clickType: 'dropdown',
        clickLocation,
      },
      e
    );
  };

  // Reset the open guard so the next open is tracked again
  const handleBlur = () => {
    openTrackedRef.current = false;
  };

  // Update the selected option key and track the chosen filter option
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const key = e.target.value;
    setSelectedKey(key);
    openTrackedRef.current = false;
    const label =
      key !== '' ? options[Number(key)]?.option_display_label : default_option_label;
    trackAdobeClick(
      {
        clickName: label || 'Filter options',
        clickType: 'dropdown-option',
        clickLocation,
      },
      e
    );
  };

  const tableRows: FileRow[] = selectedOption?.file_table_section ?? [];

  // Drop a column when every row's value for that column is empty
  const showTitle = tableRows.some((r) => !!r?.row_title);
  const showFile1 = tableRows.some((r) => !!r?.row_file_1?.url);
  const showFile2 = tableRows.some((r) => !!r?.row_file_2?.url);
  const showTable = tableRows.length > 0 && (showTitle || showFile1 || showFile2);

  // Render a file cell as a download link, or a centered placeholder when empty
  const renderFileCell = (
    fileDisplayName?: string,
    file?: FileField,
    cslpAttribute?: CSLPFieldMapping
  ) => {
    if (!file?.url) {
      return (
        <td className={cell({ class: [cellFile(), cellEmpty()].join(' ') })}>
          <span aria-hidden="true">-</span>
          <span className="sr-only">No file</span>
        </td>
      );
    }
    return (
      <td className={cell({ class: cellFile() })}>
        <Link
          href={`/api/download?url=${encodeURIComponent(file.url)}&filename=${encodeURIComponent(file?.filename ?? '')}`}
          download
          className={fileLink()}
          onClick={(e) =>
            trackAdobeDownload(
              {
                clickName: fileDisplayName || file.filename || file.title || 'File',
                documentName: file.filename || getDocumentNameFromUrl(file.url!),
                clickType: 'file-download',
                clickLocation: `${componentName}_${_metadata?.uid}`,
              },
              e
            )
          }
          {...getCSLPAttributes(cslpAttribute)}
        >
          <PlainTextWrapper
            className="min-w-0 wrap-break-word underline "
            content={fileDisplayName || file.filename || file.title}
          />
          <FileIcon filename={file.filename} className="shrink-0" />
        </Link>
      </td>
    );
  };

  return (
    <Container componentName={componentName} metaId={_metadata?.uid} id={inline_navigation_id}>
      <div className={base()}>
        <div className={filterRow()}>
          <select
            className={select()}
            value={selectedKey}
            onMouseDown={handleOpen}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label={default_option_label || 'Filter options'}
          >
            {hasPlaceholder && <option value="">{default_option_label}</option>}
            {options.map((opt, idx) => (
              <option key={idx} value={String(idx)}>
                {opt?.option_display_label}
              </option>
            ))}
          </select>
        </div>

        {showTable && (
          <div className={tableWrapper()}>
            <table className={table()}>
              <tbody>
                {tableRows.map((r, idx) => (
                  <tr key={idx} className={row()}>
                    {showTitle &&
                      (r?.row_title ? (
                        <td className={cell()}>
                          <PlainTextWrapper content={r.row_title} cslpAttribute={r.$?.row_title} />
                        </td>
                      ) : (
                        <td className={cell({ class: [cellEmpty()].join(' ') })}>-</td>
                      ))}
                    {showFile1 &&
                      renderFileCell(r.file_1_display_title, r?.row_file_1, r.$?.row_file_1)}
                    {showFile2 &&
                      renderFileCell(r.file_2_display_title, r?.row_file_2, r.$?.row_file_2)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedOption?.content && (
          <RichTextWrapper
            content={selectedOption.content}
            cslpAttribute={selectedOption.$?.content}
            className={content()}
            modalTitle={selectedOption.option_display_label}
          />
        )}
      </div>
    </Container>
  );
};

const variants = {
  Default,
};

export const FilteredList = (props: FilteredListProps) => {
  if (!props.reference?.length) return null;

  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
